import { existsSync, statSync, realpathSync } from 'node:fs';
import { dirname, join, normalize, sep } from 'node:path';

export interface ProjectRootOptions {
  readonly projectRoot?: string | undefined;
}

/**
 * Service for resolving the project root directory using a hierarchical detection strategy.
 *
 * Resolution hierarchy:
 * 1. Explicit projectRoot option (highest priority)
 * 2. PROJECT_ROOT environment variable (set by plugin commands)
 * 3. Git root detection (walk up to find .git directory)
 * 4. PWD environment variable (set by MCP server and shells)
 * 5. process.cwd() (fallback)
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class ProjectRootService {
  /**
   * Resolve project root directory using hierarchical detection.
   */
  static resolve(options?: ProjectRootOptions): string {
    // 1. Check explicit option first
    if (options?.projectRoot !== undefined && options.projectRoot !== '') {
      return this.normalize(options.projectRoot);
    }

    // 2. Check PROJECT_ROOT environment variable (plugin commands)
    const projectRootEnv = process.env['PROJECT_ROOT'];
    if (projectRootEnv !== undefined && projectRootEnv !== '') {
      // Expand shell variables that Claude Code doesn't expand in MCP env vars
      const expanded = this.expandShellVars(projectRootEnv);
      return this.normalize(expanded);
    }

    // 3. Try git root detection (most reliable for repos)
    const gitRoot = this.findGitRoot(process.cwd());
    if (gitRoot !== null) {
      return gitRoot;
    }

    // 4. Check PWD environment variable (MCP server, shells) - only if not in a git repo
    const pwdEnv = process.env['PWD'];
    if (pwdEnv !== undefined && pwdEnv !== '') {
      return this.normalize(pwdEnv);
    }

    // 5. Fallback to process.cwd()
    return process.cwd();
  }

  /**
   * Find git repository root by walking up the directory tree looking for .git
   */
  static findGitRoot(startPath: string): string | null {
    let currentPath = normalize(startPath);
    const root = normalize(sep); // Root filesystem (/ on Unix, C:\ on Windows)

    // Walk up directory tree
    while (currentPath !== root) {
      const gitPath = join(currentPath, '.git');

      if (existsSync(gitPath)) {
        try {
          const stats = statSync(gitPath);
          // .git can be a directory (normal repo) or file (submodule/worktree)
          if (stats.isDirectory() || stats.isFile()) {
            return currentPath;
          }
        } catch {
          // Ignore stat errors, continue searching
        }
      }

      // Move up one directory
      const parentPath = dirname(currentPath);
      if (parentPath === currentPath) {
        // Reached root without finding .git
        break;
      }
      currentPath = parentPath;
    }

    return null;
  }

  /**
   * Normalize path by resolving symlinks and normalizing separators
   */
  static normalize(path: string): string {
    try {
      // Resolve symlinks to real path
      const realPath = realpathSync(path);
      // Normalize separators
      return normalize(realPath);
    } catch {
      // If realpath fails (path doesn't exist), just normalize
      return normalize(path);
    }
  }

  /**
   * Expand shell variable references like ${PWD} that aren't expanded by Claude Code
   * when passing env vars from .mcp.json to the MCP server process.
   */
  static expandShellVars(value: string): string {
    return value.replace(/\$\{(\w+)\}/g, (match, name: string) => {
      return process.env[name] ?? match;
    });
  }

  /**
   * Validate that a path exists and is a directory
   */
  static validate(path: string): boolean {
    try {
      const stats = statSync(path);
      return stats.isDirectory();
    } catch {
      return false;
    }
  }
}
